import { getAccessToken, readAuthSessionMeta, toWorkspaceSession } from './authSession';
import { isDemoAuth, isJwtAuth, isJwtOnly } from './authMode';
import { readDemoSession } from './demoSession';
import { readEnvIdentity } from './workspaces';

export class IdentityNotConfiguredError extends Error {
  constructor(workspaceId) {
    super('Demo workspace is not configured');
    this.name = 'IdentityNotConfiguredError';
    this.workspaceId = workspaceId;
  }
}

export class WorkspaceIdentityMismatchError extends Error {
  constructor(requestedWorkspaceId, currentWorkspaceId) {
    super(
      `The ${requestedWorkspaceId} API cannot run while the ${currentWorkspaceId} workspace is selected.`
    );
    this.name = 'WorkspaceIdentityMismatchError';
    this.requestedWorkspaceId = requestedWorkspaceId;
    this.currentWorkspaceId = currentWorkspaceId;
  }
}

export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
    this.status = 401;
  }
}

export class ReadOnlyForbiddenError extends Error {
  constructor(
    message = 'Investor accounts are read-only and cannot start or complete processes'
  ) {
    super(message);
    this.name = 'ReadOnlyForbiddenError';
    this.status = 403;
  }
}

function sessionMatchesApiWorkspace(sessionWorkspaceId, apiWorkspaceId) {
  return sessionWorkspaceId === apiWorkspaceId;
}

function resolveJwtIdentity(workspaceId) {
  const token = getAccessToken();
  const meta = readAuthSessionMeta();
  const jwtSession = toWorkspaceSession(meta);
  if (!jwtSession || !token) {
    return null;
  }
  if (!sessionMatchesApiWorkspace(jwtSession.workspaceId, workspaceId)) {
    throw new WorkspaceIdentityMismatchError(workspaceId, jwtSession.workspaceId);
  }
  return {
    userId: jwtSession.userId || '',
    orgId: jwtSession.orgId || '',
    configured: Boolean(token && jwtSession.orgId),
    accessToken: token,
    role: jwtSession.role || '',
  };
}

function resolveDemoIdentity(workspaceId) {
  const session = readDemoSession();
  if (session && !sessionMatchesApiWorkspace(session.workspaceId, workspaceId)) {
    throw new WorkspaceIdentityMismatchError(workspaceId, session.workspaceId);
  }
  if (session && sessionMatchesApiWorkspace(session.workspaceId, workspaceId)) {
    return {
      userId: session.userId || '',
      orgId: session.orgId || '',
      configured: Boolean(session.configured),
      role: session.role || '',
    };
  }
  return { ...readEnvIdentity(workspaceId), role: '' };
}

export function resolveWorkspaceIdentity(workspaceId) {
  // Dual / JWT: prefer Bearer when a live access token exists
  if (isJwtAuth()) {
    const jwtIdentity = resolveJwtIdentity(workspaceId);
    if (jwtIdentity) {
      return jwtIdentity;
    }
    if (isJwtOnly()) {
      return {
        userId: '',
        orgId: '',
        configured: false,
        accessToken: '',
        role: '',
      };
    }
  }
  if (isDemoAuth()) {
    return resolveDemoIdentity(workspaceId);
  }
  return { userId: '', orgId: '', configured: false, role: '' };
}

export function peekWorkspaceIdentity(workspaceId) {
  try {
    return resolveWorkspaceIdentity(workspaceId);
  } catch (err) {
    if (err instanceof WorkspaceIdentityMismatchError) {
      return { userId: '', orgId: '', configured: false, accessToken: '', role: '' };
    }
    throw err;
  }
}

export function requireIdentityHeaders(workspaceId) {
  const identity = resolveWorkspaceIdentity(workspaceId);
  if (identity.accessToken) {
    if (!identity.configured) {
      throw new IdentityNotConfiguredError(workspaceId);
    }
    return {
      Authorization: `Bearer ${identity.accessToken}`,
    };
  }
  if (isJwtOnly()) {
    throw new UnauthorizedError('Sign in required');
  }
  if (!identity.configured || !identity.userId || !identity.orgId) {
    throw new IdentityNotConfiguredError(workspaceId);
  }
  return {
    'X-User-Id': identity.userId,
    'X-Org-Id': identity.orgId,
  };
}

/** Send expired or missing sessions back to sign-in. Login failures stay on /signin. */
export function redirectToSignIn() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (
    path === '/signin' ||
    path.startsWith('/signin/') ||
    path.startsWith('/invite') ||
    path === '/auth/callback'
  ) {
    return;
  }
  window.location.assign('/signin');
}
